import type { Node as NvlNode, Relationship as NvlRelationship } from '@neo4j-nvl/base';
import { DEFAULT_NVL_NODE_RADIUS } from '@nx/state';

import { gray } from '../../styles/colors';
import {
  type Node as BloomNode,
  type Relationship as BloomRelationship,
  type NodeGroup,
  type RelationshipGroup,
  isNodeGroup,
  isRelationshipGroup,
} from '../../types/graph';
import type { StyleRule } from '../../types/style';
import { DEFAULT_NVL_DISABLED_COLOR } from './const';
import { getGroupIconUrl, getIconAssetUrl } from './iconUtils';

export const sizeToRadius = (size: number) => Math.sqrt(size) * DEFAULT_NVL_NODE_RADIUS;
export const radiusToSize = (radius: number) => Math.pow(radius / DEFAULT_NVL_NODE_RADIUS, 2);

export interface MapperProps {
  colorForNodeMapper: (node: BloomNode) => NvlNode['color'];
  getUniqueValuesForStyleRules: (nodes: BloomNode[]) => Record<string, Partial<StyleRule>>;
  captionSizeForNodeMapper: (node: BloomNode) => NvlNode['captionSize'];
  captionAlignForNodeMapper: (node: BloomNode) => NvlNode['captionAlign'];
  captionsForNodeMapper: (node: BloomNode) => NvlNode['captions'];
  captionSizeForRelationshipMapper: (rel: BloomRelationship) => NvlRelationship['captionSize'];
  captionAlignForRelationshipMapper: (rel: BloomRelationship) => NvlRelationship['captionAlign'];
  captionsForRelationshipMapper: (rel: BloomRelationship) => NvlRelationship['captions'];
  sizeForNodeMapper: (node: BloomNode) => NvlNode['size'];
  iconForNodeMapper: (node: BloomNode) => NvlNode['icon'];
  colorForRelMapper: (rel: BloomRelationship) => NvlRelationship['color'];
  sizeForRelMapper: (rel: BloomRelationship) => NvlRelationship['width'];
  getAssetUrl: (target: string) => string;
  nodeSelectedMap: Record<string, boolean>;
  activatedNodeMap: Record<string, boolean>;
  nodeDisabledMap: Record<string, boolean>;
  relDisabledMap: Record<string, boolean>;
  relSelectedMap: Record<string, boolean>;
  canvasBackgroundColor: string;
  overlayIconColor: string;
}

export const mapNodeToNvl =
  (props: MapperProps) =>
  (node: BloomNode | NodeGroup): NvlNode => {
    const { id } = node;
    const isDisabled = props.nodeDisabledMap[id] !== undefined;

    const mappedNode: NvlNode = {
      id,
      pinned: node.pinned,
      activated: props.activatedNodeMap[id] !== undefined,
      selected: props.nodeSelectedMap[id] !== undefined,
      disabled: isDisabled,
    };

    if (isNodeGroup(node)) {
      const captions = new Set<string>();
      node.labelStrings.forEach(captions.add, captions);
      mappedNode.color = gray;
      mappedNode.captions = Array.from(captions).map((value) => ({ value }));
      mappedNode.size = 50;
      mappedNode.overlayIcon = {
        url: getGroupIconUrl(props.overlayIconColor, node.groupedIds.length, props.canvasBackgroundColor),
        size: 0.4,
        position: [0.75, -0.75],
      };
    } else {
      const mappedIcon = props.iconForNodeMapper(node) ?? '';
      mappedNode.color = isDisabled ? DEFAULT_NVL_DISABLED_COLOR : props.colorForNodeMapper(node);
      mappedNode.icon = getIconAssetUrl(mappedIcon, props.getAssetUrl);
      mappedNode.captionSize = props.captionSizeForNodeMapper(node);
      mappedNode.captionAlign = props.captionAlignForNodeMapper(node);
      mappedNode.captions = props.captionsForNodeMapper(node);
      mappedNode.size = sizeToRadius(props.sizeForNodeMapper(node) ?? DEFAULT_NVL_NODE_RADIUS);
    }

    if (node.placement && node.placement.x && node.placement.y) {
      mappedNode.x = node.placement.x;
      mappedNode.y = node.placement.y;
    }

    return mappedNode;
  };

export const mapRelationshipToNvl =
  (props: MapperProps) =>
  (
    rel: BloomRelationship | RelationshipGroup,
    relInventory: Record<string, BloomRelationship>,
    nodeIdToGroupIdMap: Record<string, string>,
  ): NvlRelationship => {
    const { id, startId, endId } = rel;
    const isDisabled = props.relDisabledMap[id] !== undefined;

    if (isRelationshipGroup(rel) && rel.relationships.length > 1) {
      const from = nodeIdToGroupIdMap[startId] ?? startId;
      const to = nodeIdToGroupIdMap[endId] ?? endId;

      return {
        id,
        from,
        to,
        selected: props.relSelectedMap[id] !== undefined,
        overlayIcon: {
          url: getGroupIconUrl(props.overlayIconColor, rel.relationships.length, props.canvasBackgroundColor),
          size: 4,
          position: [0, 0.25],
        },
      };
    }

    const relationshipToMap = isRelationshipGroup(rel) ? relInventory[rel.relationships[0]!]! : rel;
    const from = nodeIdToGroupIdMap[relationshipToMap.startId] ?? relationshipToMap.startId;
    const to = nodeIdToGroupIdMap[relationshipToMap.endId] ?? relationshipToMap.endId;

    return {
      id,
      from,
      to,
      captionSize: props.captionSizeForRelationshipMapper(relationshipToMap),
      captionAlign: props.captionAlignForRelationshipMapper(relationshipToMap),
      captions: props.captionsForRelationshipMapper(relationshipToMap),
      selected: props.relSelectedMap[id] !== undefined,
      disabled: isDisabled,
      color: props.colorForRelMapper(relationshipToMap),
      width: props.sizeForRelMapper(relationshipToMap),
    };
  };
